import { Icon } from '@ps/isp-web-component-library';
import { Ninety } from '@ps/isp-web-component-library/icons/Ninety';
import { PriceBoost } from '@ps/isp-web-component-library/icons/PriceBoost';
import { StarSubIcon } from '@ps/isp-web-component-library/icons/StarSubIcon';
import { TwoUpIcon } from '@ps/isp-web-component-library/icons/TwoUpIcon';
import type {
  Handicap,
  MarketType,
  Odds as OddsType,
  PlaceTerms
} from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import { getIsMatchOddsNinetyMarket, getIsTwoUpMarket } from '@ps/web-sports-utils';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { EachWayPlaceTerms, Odds } from '@swf/sports/components';
import { OpportunityDescription } from '@swf/sports/components/OpportunityDescription/OpportunityDescription';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { getEventTypeIcon } from '@swf/sports/utils/getEventTypeIcon';
import getRunnerNameWithHandicap from '@swf/sports/utils/getRunnerNameWithHandicap';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';

import {
  TXTWCL_EW,
  TXTWCL_Each_Way,
  TXTWCL_Match_Odds_90,
  TXTWCL_Match_Odds___2_Up,
  TXTWCL_SP,
  TXTWCL_Star_Sub_enabled_selection,
  TXTWCL_odds_boost
} from './BetDetails.messages.json';
import styles from './BetDetails.scss';

interface Props {
  name: string;
  handicap: Handicap;
  description: string;
  odds?: OddsType;
  isEachWaySelected?: boolean;
  placeTerms?: PlaceTerms;
  venue?: string;
  eventTypeId?: EventTypeId;
  marketTime?: string;
  marketType?: MarketType;
  isStartingPriceOpportunity: boolean;
  isStarSubEnabled?: boolean;
  isEventInPlay?: boolean;
}

const BetDetails: React.FC<Props> = ({
  name,
  description,
  odds,
  handicap,
  isEachWaySelected,
  placeTerms,
  venue,
  eventTypeId,
  marketTime,
  isStartingPriceOpportunity,
  isStarSubEnabled,
  marketType,
  isEventInPlay
}) => {
  const { formatMessage } = useRegulatoryIntl();

  const isMatchOddsNinetyMarket = getIsMatchOddsNinetyMarket(marketType);
  const isTwoUpMarket = getIsTwoUpMarket(marketType);

  return (
    <div className={styles.wrapper}>
      <span className={styles.iconWrapper}>
        <Icon icon={getEventTypeIcon(eventTypeId)} size="x1" />
      </span>
      <div className={styles.eventInfoWrapper}>
        <div className={styles.eventName}>
          {getRunnerNameWithHandicap({ runnerName: name, handicap, runnerResultType: undefined })}
          {isStarSubEnabled ? (
            <Icon
              icon={StarSubIcon}
              className={styles.marketIcon}
              label={formatMessage(TXTWCL_Star_Sub_enabled_selection)}
              size="xs"
            />
          ) : null}
          {isMatchOddsNinetyMarket ? (
            <Icon
              icon={Ninety}
              className={styles.marketIcon}
              label={formatMessage(TXTWCL_Match_Odds_90)}
              size="xs"
            />
          ) : null}
          {isTwoUpMarket ? (
            <Icon
              icon={TwoUpIcon}
              className={styles.marketIcon}
              label={formatMessage(TXTWCL_Match_Odds___2_Up)}
              size="xs"
            />
          ) : null}
          {isOddsBoostMarket(marketType) && (
            <Icon
              className={styles.marketIcon}
              icon={PriceBoost}
              label={formatMessage(TXTWCL_odds_boost)}
              size="xs"
            />
          )}
          {odds ? (
            <>
              {' '}
              @ <Odds {...odds} />
            </>
          ) : isStartingPriceOpportunity ? (
            <>
              {' '}
              @ <Translation {...TXTWCL_SP} />
            </>
          ) : null}
          {isEachWaySelected ? (
            <>
              {' '}
              <Translation {...TXTWCL_EW} />{' '}
            </>
          ) : null}
        </div>
        <OpportunityDescription
          description={description}
          venue={venue}
          marketTime={marketTime}
          isEventInPlay={isEventInPlay}
        />
        {isEachWaySelected && (
          <span>
            {placeTerms ? (
              <EachWayPlaceTerms placeTerms={placeTerms} />
            ) : (
              <Translation {...TXTWCL_Each_Way} />
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default BetDetails;
